import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";

// Material Imports
import {
  Chip,
  Select,
  MenuItem,
  Button,
  TextField,
  InputLabel,
  FormLabel,
  FormControl,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import TodayOutlined from "@mui/icons-material/TodayOutlined";
import Add from "@mui/icons-material/Add";

import { makeStyles } from "@mui/styles";

// Import CSS
import "./TaskModal.css";

import { TASK_STATUS_COLOR_MAP, TASK_STATUS_OPTIONS } from "static/constants";
import useTaskModal from "./useTaskModal";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import SearchPatient from "components/pages/manualUpload/forms/SearchPatient";
import { getFormattedDate } from "utils/date";
import { DATE_FORMAT } from "static/date";
import { Close } from "@mui/icons-material";
import { isValidArray } from "utils/array";

const validationSchema = Yup.object().shape({
  account: Yup.string().required("Account is required"),
  patientName: Yup.string().required("Patient Name is required"),
  status: Yup.string().required("Status is required"),
  description: Yup.string()
    .required("Task Description is required")
    .test(
      "no-whitespace-only",
      "Task Description cannot contain only spaces",
      (value) => !value || value.trim().length > 0
    ),
  notes: Yup.array().of(
    Yup.object().shape({
      note: Yup.string().test(
        "no-whitespace-only",
        "Communication Notes cannot contain only spaces",
        (value) => !value || value.trim().length > 0
      ),
    })
  ),
});

const useStyles = makeStyles(() => ({
  label: {
    fontFamily: "Roboto",
    fontWeight: 400,
    fontStyle: "normal",
    fontSize: "12px",
    marginBottom: "8px",
  },
  dateInfoBanner: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#EDF7ED",
    borderRadius: "4.66px",
    padding: "8px",
    fontFamily: "Roboto",
    fontWeight: 400,
    fontSize: "12px",
    marginTop: "10px",
    marginBottom: "5px",
  },
  disabledInput: {
    "& .Mui-disabled": {
      backgroundColor: "#ECECEC",
      borderColor: "#E0E0E0",
    },
  },
  todayIcon: {
    color: "#757575",
    width: "12px",
    height: "13px",
  },
  statusGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  statusChip: {
    flex: "1 1 calc(25% - 6px)",
    justifyContent: "center",
    minWidth: 0,
    borderRadius: "50px",
    "& .MuiChip-label": {
      fontSize: "10px !important",
    },
  },
  patientCard: {
    border: "1px solid #BFD685",
    borderRadius: "4.66px",
    backgroundColor: "#F7FBEE",
    padding: "8px 16px",
  },
  patientName: {
    color: "#212121",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "Roboto",
    marginBottom: "5px",
  },
  dobSeparator: {
    margin: "0 4px",
  },
  closeIcon: {
    color: "#757575",
    width: "16px",
    height: "16px",
    cursor: "initial",
  },
  patientCardDisabled: {
    backgroundColor: "#ECECEC",
    borderColor: "#E0E0E0",
  },
  notesField: {
    "& .MuiOutlinedInput-root fieldset": {
      borderColor: "#e0e0e0",
    },
  },
  notesHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  noteCard: {
    backgroundColor: "#F5F7FB",
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    padding: "10px 12px",
    marginBottom: "8px",
  },
  noteText: {
    color: "#212121",
    fontSize: "14px",
    fontFamily: "Roboto",
    fontWeight: 400,
    marginBottom: "4px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  noteTimestamp: {
    fontFamily: "Roboto",
    fontSize: "11px",
    color: "#757575",
  },
}));

const TaskModal = ({ canChangeAccount = true, selectedTab }) => {
  const {
    taskModalOpen,
    taskModalTitle,
    selectedTask,
    processing,
    availableAccounts,
    requestType,
    theme,
    onConfirm: onTaskConfirm,
    onClose,
  } = useTaskModal(selectedTab);

  const classes = useStyles();
  const { currentUser } = useSelector((state) => state.auth);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [notesList, setNotesList] = useState([]);
  const [newNoteText, setNewNoteText] = useState("");

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      account: selectedTask?.account || "",
      status: selectedTask?.status || "",
      description: selectedTask?.description || "",
      notes:
        selectedTask?.notes?.map((n) => ({
          note: n.note,
          addedAt: n.addedAt,
        })) ??
        (selectedTask?.note
          ? [{ note: selectedTask.note, addedAt: selectedTask.createdAt }]
          : []),
      patientName: selectedTask?.patientName || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const finalNotes = newNoteText.trim()
        ? [...notesList, { note: newNoteText.trim(), addedAt: new Date() }]
        : notesList;
      const dataToSubmit = {
        ...values,
        notes: finalNotes,
        ...(selectedPatient && {
          patientId: selectedPatient._id,
          patientName: selectedPatient.name,
          patientSerialNumber: selectedPatient.serial_number,
        }),
      };
      await onTaskConfirm(dataToSubmit);
    },
  });

  useEffect(() => {
    if (requestType === "PUT") {
      setSelectedPatient(selectedTask?.patient);
      const existingNotes = selectedTask?.notes;
      const existingNote = selectedTask?.note;
      if (isValidArray(existingNotes)) {
        setNotesList(
          existingNotes.map((n) => ({
            note: n.note ?? n,
            addedAt: n.addedAt ? new Date(n.addedAt) : new Date(),
            addedBy: n.addedBy ?? currentUser?.name,
          }))
        );
      } else if (existingNote) {
        setNotesList([
          {
            note: existingNote,
            addedAt: selectedTask?.createdAt
              ? new Date(selectedTask.createdAt)
              : new Date(),
            addedBy: currentUser?.name,
          },
        ]);
      }
    } else {
      setNotesList([]);
    }
    setNewNoteText("");
  }, [requestType]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    resetForm,
    isSubmitting,
    setFieldError,
  } = formik;

  useEffect(() => {
    if (
      selectedPatient?.account &&
      values.account &&
      selectedPatient.account !== values.account
    ) {
      setFieldError(
        "account",
        `Selected patient belongs to "${selectedPatient.account}". Change the account or remove the patient.`
      );
    } else {
      setFieldError("account", undefined);
    }
  }, [values.account, selectedPatient]);

  const handleModalClose = () => {
    resetForm();
    setNotesList([]);
    setNewNoteText("");
    onClose();
  };

  const handleAddNote = () => {
    if (newNoteText.trim()) {
      setNotesList((prev) => [
        ...prev,
        {
          note: newNoteText.trim(),
          addedAt: new Date(),
          addedBy: currentUser?.name,
        },
      ]);
      setNewNoteText("");
    }
  };

  const actionButtons = [
    {
      label: "Cancel",
      variant: "outlined",
      onClick: handleModalClose,
      disabled: processing,
    },
    {
      label: requestType === "PUT" ? "Save Changes" : "Add Task",
      variant: "contained",
      onClick: handleSubmit,
      disabled: processing || !!errors.account || isSubmitting,
      isLoading: processing || isSubmitting,
      className: "ml-2",
    },
  ];

  return (
    <FormikProvider value={formik}>
      <ModalWrapper
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        show={taskModalOpen}
        onClose={handleModalClose}
        title={taskModalTitle}
        actionButtons={actionButtons}
        showContentDivider
        maxWidth="sm"
      >
        <div className={`d-flex flex-column g-1 ${classes.disabledInput}`}>
          <div className={classes.dateInfoBanner}>
            <TodayOutlined className={classes.todayIcon} />
            {requestType === "PUT"
              ? `Assigned on ${getFormattedDate(selectedTask?.created_at, "MM/DD/YY")}`
              : `Date will be recorded as today (${getFormattedDate(new Date(), "MM/DD/YY")})`}
          </div>

          <FormControl
            fullWidth
            variant="outlined"
            disabled={!canChangeAccount}
            style={{
              backgroundColor: canChangeAccount
                ? theme.palette.background.paper
                : theme.palette.type === "dark"
                  ? "#2f2f2f"
                  : "#ECECEC",
              ...(requestType === "PUT" && {
                marginTop: "15px",
              }),
            }}
            required
          >
            <InputLabel htmlFor="accounts" id="label-accounts">
              Account
            </InputLabel>
            <Select
              id="accounts"
              labelId="label-accounts"
              disabled={requestType === "PUT"}
              name="account"
              value={values.account}
              input={<OutlinedInput label="Account" />}
              onChange={handleChange}
              onBlur={() => setFieldTouched("account", true, false)}
              error={touched.account && !!errors.account}
              MenuProps={{ PaperProps: { style: { maxHeight: 240 } } }}
              required
            >
              {availableAccounts?.map((account) => (
                <MenuItem key={account?._id} value={account?.name}>
                  {account?.name}
                </MenuItem>
              ))}
            </Select>
            {touched.account && errors.account && (
              <FormHelperText error>{errors.account}</FormHelperText>
            )}
          </FormControl>

          <FormControl>
            {selectedPatient?.name ? (
              <div
                className={`${classes.patientCard} ${requestType === "PUT" ? classes.patientCardDisabled : ""} d-flex align-items-center justify-between`}
              >
                <div>
                  {selectedPatient?.name && (
                    <p className={classes.patientName}>
                      {selectedPatient?.name}
                    </p>
                  )}
                  <p className="mt-1 mb-1">
                    {selectedPatient?.dob && (
                      <span className={classes.label}>
                        DOB:
                        {getFormattedDate(
                          selectedPatient?.dob,
                          DATE_FORMAT.DEFAULT
                        )}
                      </span>
                    )}
                    {selectedPatient?.dob &&
                      selectedPatient?.evoke_id &&
                      selectedPatient?.account && (
                        <span className={classes.dobSeparator}>|</span>
                      )}
                    {selectedPatient?.evoke_id && (
                      <span className={classes.label}>
                        Evoke ID: {selectedPatient?.evoke_id}
                      </span>
                    )}
                    {(selectedPatient?.dob || selectedPatient?.evoke_id) &&
                      selectedPatient?.account && (
                        <span className={classes.dobSeparator}>|</span>
                      )}
                    {selectedPatient?.account && (
                      <span className={classes.label}>
                        Account: {selectedPatient?.account}
                      </span>
                    )}
                  </p>
                </div>
                {requestType !== "PUT" && (
                  <div>
                    <Close
                      className={classes.closeIcon}
                      onClick={
                        requestType === "PUT"
                          ? undefined
                          : () => {
                              setSelectedPatient(null);
                              setFieldValue("patientName", "");
                            }
                      }
                    />
                  </div>
                )}
              </div>
            ) : (
              <SearchPatient
                handlePatientSelect={(value) => {
                  setSelectedPatient(value);
                  if (value?.account) setFieldValue("account", value.account);
                  setFieldValue("patientName", value.name);
                }}
                account={values.account && encodeURIComponent(values.account)}
                label="Patient Name"
                hideSearchByDeviceSerialNumber={true}
                style={{ marginTop: 0, marginBottom: 0 }}
                required
                freeSolo={false}
                disabled={!values.account}
              />
            )}
            {touched.patientName && errors.patientName && (
              <FormHelperText error>{errors.patientName}</FormHelperText>
            )}
          </FormControl>

          <FormControl
            fullWidth
            error={touched.status && !!errors.status}
            className="task-modal__input"
          >
            <FormLabel id="status-label" className={classes.label}>
              Status
            </FormLabel>
            <div
              role="group"
              aria-labelledby="status-label"
              className={classes.statusGroup}
            >
              {TASK_STATUS_OPTIONS.map((status) => {
                const color = TASK_STATUS_COLOR_MAP[status.value];
                const isSelected = values.status === status.value;

                return (
                  <Chip
                    key={status.value}
                    variant="outlined"
                    size="small"
                    clickable
                    label={status.label}
                    aria-pressed={isSelected}
                    onClick={() => setFieldValue("status", status.value)}
                    className={classes.statusChip}
                    icon={
                      <span
                        className={classes.statusDot}
                        style={{ backgroundColor: color }}
                      />
                    }
                    style={{
                      borderColor: isSelected ? color : "#E0E0E0",
                      fontWeight: isSelected ? 600 : 400,
                    }}
                    sx={{
                      "& .MuiChip-label": {
                        color: isSelected ? color : "#444444",
                      },
                    }}
                  />
                );
              })}
            </div>
            {touched.status && errors.status && (
              <FormHelperText error>{errors.status}</FormHelperText>
            )}
          </FormControl>

          <FormControl>
            <FormLabel id="status-label" className={classes.label}>
              Task Description
            </FormLabel>
            <TextField
              minRows={6}
              maxRows={13}
              multiline
              fullWidth
              variant="outlined"
              id="description"
              name="description"
              data-text="report-task-note-field"
              placeholder="What needs to happen..."
              className="task-modal__input"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={touched.description && errors.description}
              error={!!(touched.description && errors.description)}
            />
          </FormControl>

          <FormControl>
            <div className={classes.notesHeader}>
              <FormLabel className={classes.label} style={{ marginBottom: 0 }}>
                Communication Notes (
                {notesList.length > 0
                  ? `${notesList.length} added`
                  : "optional"}
                )
              </FormLabel>
              <Button
                size="small"
                startIcon={<Add />}
                onClick={handleAddNote}
                disabled={processing || !newNoteText.trim()}
                style={{ textTransform: "none", fontSize: "12px" }}
                variant="outlined"
                color="secondary"
                sx={{
                  "&.Mui-disabled": {
                    border: "1px solid #969696",
                    borderRadius: "4px",
                    background: "#fff !important",
                    color: "#969696 !important",
                    height: "28px",
                    "& .MuiButton-startIcon": {
                      color: "#969696",
                    },
                  },
                }}
              >
                Add Notes
              </Button>
            </div>

            <TextField
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              variant="outlined"
              data-text="communication-note-field"
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              placeholder="Calls, messages or handoff details for the care team.."
              className={`task-modal__input ${classes.notesField}`}
            />

            {notesList?.length > 0 && (
              <div className="mt-2">
                {[...notesList]
                  .sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt))
                  .map((n, index) => (
                    <div key={index} className={classes.noteCard}>
                      <p className={classes.noteText}>{n.note}</p>
                      <span className={classes.noteTimestamp}>
                        {getFormattedDate(
                          n.addedAt,
                          DATE_FORMAT.SHORT_DATE_TIME
                        )}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </FormControl>
        </div>
      </ModalWrapper>
    </FormikProvider>
  );
};

export default TaskModal;
